import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { decodeToken, getAccessToken, logout } from '../utils/auth';

// The token's `sub` is the username (the backend puts the id in `user_id`),
// so the greeting costs no request.
function currentUsername() {
  const token = getAccessToken();
  const sub = token ? decodeToken(token)?.sub : null;
  return typeof sub === 'string' && sub ? sub : null;
}

// Sliders rather than a gear: the gear is the project-settings affordance on
// the Chapters page, and two gears meaning different things is one too many.
const SLIDERS_ICON =
  'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4';
const SIGN_OUT_ICON =
  'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1';

function MenuIcon({ d }) {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
    </svg>
  );
}

function AdminNavigation() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const username = currentUsername();

  // Close on a click anywhere else, or on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!menuRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const itemClass =
    'flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white transition-colors';

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200/50 dark:border-gray-800/50 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-4">
            <img src="/favicon-32x32.png" alt="Dissona" className="size-6" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Dissona</h2>
          </Link>

          <div className="relative" ref={menuRef}>
            {/* Account menu. Deliberately not a gear — that belongs to project
                settings on the Chapters page. */}
            <button
              type="button"
              onClick={() => setOpen((wasOpen) => !wasOpen)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="Account menu"
              className="flex items-center justify-center rounded-lg p-2 text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>

            {open && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-48 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-background-dark shadow-lg"
              >
                <p className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-800 truncate">
                  Hi {username || 'there'}
                </p>
                <Link to="/settings" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
                  <MenuIcon d={SLIDERS_ICON} />
                  Settings
                </Link>
                <button type="button" role="menuitem" onClick={logout} className={itemClass}>
                  <MenuIcon d={SIGN_OUT_ICON} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default AdminNavigation;
